import {Component} from 'react';

import {CrossTableCellColors} from 'notebook/tables/constants/CellConstants';
import {TableIndex} from 'notebook/tables/constants/TableSelectionConstants';
import {
  FullTableMap,
  OutputTableMap,
  TableLocationNameArgs,
} from 'notebook/tables/excess/EvaluationConstants';
import {TryInsertOptions} from 'notebook/tables/excess/FormulaUtil';
import {TableOpFunction, TableOpOptions} from 'notebook/tables/excess/ops/TableOpConstants';
import {
  ExternalProps as SpreadsheetEvalExternalProps,
  EnhancedProps as SpreadsheetEvalEnhancedProps,
} from 'notebook/tables/spreadsheets/withSpreadsheetEvaluation';
import {TypedValue} from 'notebook/tables/typed-values/TypedValueConstants';
import {NotePart} from 'notebook/util/NoteUtilConstants';

type TableRoot = unknown;
type TableItem = unknown;

interface MultiEditingArgs {
  index: TableIndex | null;
  editing: boolean;
  text: string;
  typedValue: TypedValue;
  selection: {cid: string; start: number; end: number} | null;
}

export type WithSpreadsheetEvaluationUserProps = SpreadsheetEvalEnhancedProps &
  SpreadsheetEvalExternalProps<TableItem, TableRoot>;

export interface WithSpreadsheetEvaluationUserState {
  [key: string]: unknown;
}

interface WithSpreadsheetEvaluationUserComponent<
  P extends WithSpreadsheetEvaluationUserProps,
  S extends WithSpreadsheetEvaluationUserState,
> extends Component<P, S> {}

// These functions are used in MediocrePlugins.getTableItemProps and tableEditablePropsFromEditor,
// which are used by DataTableEditable. DataTableEditable is used in AssayGroupResultsTable,
// which uses SpreadsheetEvalMixin, which provides these methods. When SpreadsheetEvalMixin
// is fully replaced with withSpreadsheetEvaluation, this mixin can be removed.
const WithSpreadsheetEvaluationUserMixin = {
  getRoot<P extends WithSpreadsheetEvaluationUserProps, S extends WithSpreadsheetEvaluationUserState>(
    this: WithSpreadsheetEvaluationUserComponent<P, S>,
    props: P
  ): TableRoot {
    // @ts-expect-error This shouldn't need the props plumbed but we're doing it anyways
    return this.props.getRoot!(props);
  },

  getCid<P extends WithSpreadsheetEvaluationUserProps, S extends WithSpreadsheetEvaluationUserState>(
    this: WithSpreadsheetEvaluationUserComponent<P, S>,
    item: NotePart
  ): string {
    return this.props.getCid!(item);
  },

  getContent<P extends WithSpreadsheetEvaluationUserProps, S extends WithSpreadsheetEvaluationUserState>(
    this: WithSpreadsheetEvaluationUserComponent<P, S>,
    item: NotePart
  ): unknown {
    return this.props.getContent!(item);
  },

  getSpreadsheetTableName<
    P extends WithSpreadsheetEvaluationUserProps,
    S extends WithSpreadsheetEvaluationUserState,
  >(this: WithSpreadsheetEvaluationUserComponent<P, S>, item: NotePart): string {
    return this.props.getSpreadsheetTableName!(item);
  },

  setMultiEditingForTable<
    P extends WithSpreadsheetEvaluationUserProps,
    S extends WithSpreadsheetEvaluationUserState,
  >(
    this: WithSpreadsheetEvaluationUserComponent<P, S>,
    {tableName, documentName}: TableLocationNameArgs,
    args: MultiEditingArgs
  ): void {
    this.props.setMultiEditingForTable({tableName, documentName}, args);
  },

  tryInsert<P extends WithSpreadsheetEvaluationUserProps, S extends WithSpreadsheetEvaluationUserState>(
    this: WithSpreadsheetEvaluationUserComponent<P, S>,
    {tableName, documentName}: TableLocationNameArgs,
    e: TryInsertOptions
  ): boolean {
    return this.props.tryInsert({tableName, documentName}, e);
  },

  canInsert<P extends WithSpreadsheetEvaluationUserProps, S extends WithSpreadsheetEvaluationUserState>(
    this: WithSpreadsheetEvaluationUserComponent<P, S>,
    e: TryInsertOptions
  ): boolean {
    return this.props.canInsert(e);
  },

  performTableOp<P extends WithSpreadsheetEvaluationUserProps, S extends WithSpreadsheetEvaluationUserState>(
    this: WithSpreadsheetEvaluationUserComponent<P, S>,
    {tableName, op, options}: {tableName: string; op: TableOpFunction; options?: TableOpOptions}
  ): void {
    this.props.performTableOp({tableName, op, options});
  },

  isTableNameUsed<P extends WithSpreadsheetEvaluationUserProps, S extends WithSpreadsheetEvaluationUserState>(
    this: WithSpreadsheetEvaluationUserComponent<P, S>,
    tableName: string
  ): boolean {
    return this.props.isTableNameUsed(tableName);
  },

  getDataForTableItemProps<
    P extends WithSpreadsheetEvaluationUserProps,
    S extends WithSpreadsheetEvaluationUserState,
  >(
    this: WithSpreadsheetEvaluationUserComponent<P, S>
  ): {
    tableNamespace: string;
    computedTables: OutputTableMap | null;
    cellColors: CrossTableCellColors;
    editingTable: string;
    selectorCounts: {[selectorId: number]: number};
    fullTableMap: FullTableMap;
  } {
    return {
      tableNamespace: this.props.tableNamespace,
      computedTables: this.props.computedTables,
      cellColors: this.props.cellColors,
      editingTable: this.props.editingTable!,
      selectorCounts: this.props.selectorCounts,
      fullTableMap: this.props.fullTableMap,
    };
  },
};

export default WithSpreadsheetEvaluationUserMixin;
